import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { API_CONFIG } from '../config/api';
import type { StudentOptionsResponse } from '../config/api';
import { getMyGroups, GroupResponse } from '../logic/groupCreation/groupApi';
import { getGroupOverview, GroupOverviewResponse } from '../logic/groups/groupOverviewApi';
import { getGroupTrends, TrendsDataPoint } from '../logic/groups/groupTrendsApi';
import {
  addMonths,
  getMonthYearDisplay,
  generateCalendarGrid,
  formatDateKey,
  canNavigate
} from '../logic/calendar/calendarUtils';
import {
  fetchFolderGroupTree,
  flattenTreeToGroupIds,
  searchTree,
  getAllNodeIds,
  FolderTreeNode
} from '../logic/reportFilters/reportFiltersLogic';
import Modal from '../components/Modal';
import SelectDropdown from '../components/SelectDropdown';

interface ConsultantReportsPageProps {
  scope?: 'consultant' | 'enterprise';
}

const ConsultantReportsPage: React.FC<ConsultantReportsPageProps> = ({ scope = 'consultant' }) => {
  const { isDarkMode } = useTheme();
  const { token } = useAuth();
  const navigate = useNavigate();

  const GROUP_STORAGE_KEY = `${scope}ReportsSelectedGroupId`;
  const AUDIENCE_ALL = 'all';

  // Group management state
  const [groups, setGroups] = useState<GroupResponse[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsError, setGroupsError] = useState<string | null>(null);

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(() =>
    localStorage.getItem(GROUP_STORAGE_KEY)
  );

  const [overview, setOverview] = useState<GroupOverviewResponse['data'] | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  const [trends, setTrends] = useState<TrendsDataPoint[]>([]);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [trendsError, setTrendsError] = useState<string | null>(null);

  const selectedGroup = useMemo(
    () => (selectedGroupId && selectedGroupId !== AUDIENCE_ALL)
      ? groups.find((g) => g.id === selectedGroupId) || null
      : null,
    [groups, selectedGroupId]
  );

  // Report builder state
  const [reportOwnership, setReportOwnership] = useState<'locally_owned' | 'enterprise_owned'>('locally_owned');
  const [reportCourseFilter, setReportCourseFilter] = useState<string>('all');
  const [reportYearFilter, setReportYearFilter] = useState<string>('all');
  const [studentOptions, setStudentOptions] = useState<StudentOptionsResponse>({ courses: [], yearByCourse: {} });


  // Advanced Filters (Locally Owned)
  const [folderTree, setFolderTree] = useState<FolderTreeNode[]>([]);
  const [folderTreeLoading, setFolderTreeLoading] = useState(false);
  const [selectedFilterIds, setSelectedFilterIds] = useState<string[]>([]); // Selected IDs (folders or groups)
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filterSearchQuery, setFilterSearchQuery] = useState('');

  const [lastUpdated] = useState(new Date().toLocaleString());

  // When Enterprise owned, filter groups by course and year; otherwise use all groups
  const filteredGroups = useMemo(() => {

    if (reportOwnership !== 'enterprise_owned') {
      // Locally owned: filter by selected folders/groups from the tree
      if (selectedFilterIds.length === 0) return groups;

      // Get all group IDs implied by the selection (resolving folders to their child groups)
      const allowedGroupIds = flattenTreeToGroupIds(folderTree, selectedFilterIds);
      return groups.filter(g => allowedGroupIds.includes(g.id));
    }
    return groups.filter(
      (g) =>
        (reportCourseFilter === 'all' || (g.course != null && g.course === reportCourseFilter)) &&
        (reportYearFilter === 'all' || (g.yearLevel != null && g.yearLevel === reportYearFilter))
    );
  }, [groups, reportOwnership, reportCourseFilter, reportYearFilter]);

  // When "All course" is selected, year options = years of the course with the fewest years (min max year across courses)
  const yearOptionsWhenAllCourses = useMemo(() => {
    const courses = studentOptions.courses;
    const ybc = studentOptions.yearByCourse;
    if (!courses.length) return [];
    let minYears: string[] = ybc[courses[0]] || [];
    for (let i = 1; i < courses.length; i++) {
      const years = ybc[courses[i]] || [];
      if (years.length < minYears.length) minYears = years;
    }
    return minYears;
  }, [studentOptions.courses, studentOptions.yearByCourse]);

  // Date range (From/To) - single source of truth for time frame; default last 30 days to today
  const [dateRangeStart, setDateRangeStart] = useState<Date | null>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  });
  const [dateRangeEnd, setDateRangeEnd] = useState<Date | null>(() => new Date());

  // Two calendar cursors for From and To
  const [calendarCursorFrom, setCalendarCursorFrom] = useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  });
  const [calendarCursorTo, setCalendarCursorTo] = useState<Date>(() => new Date());

  // Modal state for date pickers
  const [modalFromOpen, setModalFromOpen] = useState(false);
  const [modalToOpen, setModalToOpen] = useState(false);

  // Filter handlers
  const handleToggleFilter = (id: string) => {
    setSelectedFilterIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleClearAllFilters = () => {
    setSelectedFilterIds([]);
  };

  // Helper to find name of a selected ID in the tree
  const findNodeName = (id: string, nodes: FolderTreeNode[]): string | null => {
    for (const node of nodes) {
      if (node.id === id) return node.name;
      const childName = findNodeName(id, node.children);
      if (childName) return childName;
    }
    return null;
  };

  // Recursive tree renderer for the modal
  const renderTreeNodes = (nodes: FolderTreeNode[], depth = 0) => {
    return nodes.map(node => (
      <React.Fragment key={node.id}>
        <div
          className="consultant-reports__filter-tree-item"
          style={{ paddingLeft: `${depth * 1.5}rem` }}
        >
          <label className="consultant-reports__filter-tree-label-wrapper">
            <input
              type="checkbox"
              className="consultant-reports__filter-tree-checkbox"
              checked={selectedFilterIds.includes(node.id)}
              onChange={() => handleToggleFilter(node.id)}
            />
            <span className="material-symbols-outlined consultant-reports__filter-tree-icon">
              {node.type === 'folder' ? 'folder' : 'group'}
            </span>
            <span className="consultant-reports__filter-tree-name">{node.name}</span>
          </label>
        </div>
        {node.children.length > 0 && renderTreeNodes(node.children, depth + 1)}
      </React.Fragment>
    ));
  };

  // Filter the tree for display in modal
  const displayedTree = useMemo(() =>
    searchTree(folderTree, filterSearchQuery),
    [folderTree, filterSearchQuery]);

  // Fetch groups on component mount
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setGroupsLoading(true);
        setGroupsError(null);

        const TOKEN = token || localStorage.getItem('token');
        if (!TOKEN) {
          navigate('/login');
          return;
        }

        let loadedGroups: GroupResponse[] = [];

        if (scope === 'enterprise') {
          // For enterprise: fetch all groups across the enterprise and its sub-branches
          try {
            const enterpriseUrl = `${API_CONFIG.BASE_URL}/api/groups/enterprise/all-groups`;
            const response = await fetch(enterpriseUrl, {
              headers: {
                'Authorization': `Bearer ${TOKEN}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              loadedGroups = data.data?.groups || [];
            } else if (response.status === 404) {
              // Fallback: fetch consultant groups if endpoint doesn't exist
              const fallbackResponse = await getMyGroups(TOKEN, 1, 1000);
              loadedGroups = fallbackResponse.data.groups || [];
            } else {
              throw new Error('Failed to fetch enterprise groups');
            }
          } catch (err) {
            console.warn('Enterprise groups endpoint failed, falling back to consultant groups:', err);
            // Fallback to consultant groups
            const fallbackResponse = await getMyGroups(TOKEN, 1, 1000);
            loadedGroups = fallbackResponse.data.groups || [];
          }
        } else {
          // For consultant: fetch only consultant's groups
          const response = await getMyGroups(TOKEN, 1, 1000);
          loadedGroups = response.data.groups || [];
        }

        setGroups(loadedGroups);

        const stored = localStorage.getItem(GROUP_STORAGE_KEY);
        const storedStillValid = stored && (stored === AUDIENCE_ALL || loadedGroups.some((g) => g.id === stored));

        if (storedStillValid) {
          setSelectedGroupId(stored);
        } else if (scope === 'enterprise') {
          // Default enterprise reports to All Groups
          setSelectedGroupId(AUDIENCE_ALL);
          localStorage.setItem(GROUP_STORAGE_KEY, AUDIENCE_ALL);
        } else if (loadedGroups.length > 0) {
          setSelectedGroupId(loadedGroups[0].id);
          localStorage.setItem(GROUP_STORAGE_KEY, loadedGroups[0].id);
        } else {
          setSelectedGroupId(null);
          localStorage.removeItem(GROUP_STORAGE_KEY);
        }
      } catch (e: any) {

        setGroups([]);
        setGroupsError(e?.message || 'Failed to load groups.');
      } finally {
        setGroupsLoading(false);
      }
    };

    fetchGroups();
  }, [navigate, scope, token]);

  // Fetch folder/group tree when "Locally owned" is selected
  useEffect(() => {
    if (reportOwnership !== 'locally_owned' || scope === 'enterprise') {
      setFolderTree([]);
      setSelectedFilterIds([]);
      return;
    }

    const loadTree = async () => {
      try {
        setFolderTreeLoading(true);
        const TOKEN = token || localStorage.getItem('token');
        if (!TOKEN) {
          navigate('/login');
          return;
        }
        const tree = await fetchFolderGroupTree(TOKEN);
        setFolderTree(tree);
      } catch (error) {
        console.error('Error fetching folder tree:', error);
        setFolderTree([]);
      } finally {
        setFolderTreeLoading(false);
      }
    };

    loadTree();
  }, [reportOwnership, navigate, scope, token]);

  // Fetch student options (courses, yearByCourse) when report type is Enterprise owned
  useEffect(() => {
    if (reportOwnership !== 'enterprise_owned') {
      setStudentOptions({ courses: [], yearByCourse: {} });
      return;
    }
    const fetchStudentOptions = async () => {
      try {
        const TOKEN = token || localStorage.getItem('token');
        if (!TOKEN) return;
        const url = `${API_CONFIG.ENDPOINTS.USERS_ADMIN_STUDENT_OPTIONS}?ownership=enterpriseOwned`;
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${TOKEN}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) throw new Error('Failed to load options');
        const json = await response.json();
        const data = (json.data || {}) as StudentOptionsResponse;
        setStudentOptions({ courses: data.courses || [], yearByCourse: data.yearByCourse || {} });
      } catch {
        setStudentOptions({ courses: [], yearByCourse: {} });
      }
    };
    fetchStudentOptions();
  }, [reportOwnership, token]);

  // For enterprise scope, automatically use enterprise_owned mode
  useEffect(() => {
    if (scope === 'enterprise') {
      setReportOwnership('enterprise_owned');
    }
  }, [scope]);

  // When Enterprise owned (consultant scope only), no Audience Segment dropdown: always use "All"
  useEffect(() => {
    if (reportOwnership === 'enterprise_owned' && scope !== 'enterprise') {
      setSelectedGroupId(AUDIENCE_ALL);
      localStorage.setItem(GROUP_STORAGE_KEY, AUDIENCE_ALL);
      return;
    }
    if (!selectedGroupId || selectedGroupId === AUDIENCE_ALL) return;
    const inFiltered = filteredGroups.some((g) => g.id === selectedGroupId);
    if (!inFiltered && filteredGroups.length > 0) {
      setSelectedGroupId(filteredGroups[0].id);
      localStorage.setItem(GROUP_STORAGE_KEY, filteredGroups[0].id);
    } else if (!inFiltered) {
      setSelectedGroupId(AUDIENCE_ALL);
      localStorage.setItem(GROUP_STORAGE_KEY, AUDIENCE_ALL);
    }
  }, [reportOwnership, filteredGroups, selectedGroupId, scope]);

  // When "All course" is selected, keep reportYearFilter in sync with yearOptionsWhenAllCourses (e.g. after options refetch)
  useEffect(() => {
    if (reportCourseFilter !== 'all' || reportYearFilter === 'all') return;
    if (yearOptionsWhenAllCourses.length > 0 && !yearOptionsWhenAllCourses.includes(reportYearFilter)) {
      setReportYearFilter('all');
    }
  }, [reportCourseFilter, reportYearFilter, yearOptionsWhenAllCourses]);

  // Fetch overview data when selected group changes (skip when "All" or no group)
  useEffect(() => {
    if (!selectedGroupId || selectedGroupId === AUDIENCE_ALL) {
      setOverview(null);
      return;
    }

    const fetchOverview = async () => {
      try {
        setOverviewLoading(true);
        setOverviewError(null);

        const TOKEN = token || localStorage.getItem('token');
        if (!TOKEN) {
          navigate('/login');
          return;
        }

        const response = await getGroupOverview(TOKEN, selectedGroupId);
        setOverview(response.data);
      } catch (e: any) {

        setOverview(null);
        setOverviewError(e?.message || 'Failed to load group overview.');
      } finally {
        setOverviewLoading(false);
      }
    };

    fetchOverview();
  }, [navigate, selectedGroupId, token]);

  // Fetch trends: From/To date range is the only time frame. "All" = fetch all groups and merge.
  useEffect(() => {
    if (!dateRangeStart || !dateRangeEnd) {
      setTrends([]);
      return;
    }

    const startDateStr = dateRangeStart.toISOString().split('T')[0];
    const endDateStr = dateRangeEnd.toISOString().split('T')[0];

    const fetchTrends = async () => {
      try {
        setTrendsLoading(true);
        setTrendsError(null);

        const TOKEN = token || localStorage.getItem('token');
        if (!TOKEN) {
          navigate('/login');
          return;
        }

        if (selectedGroupId === AUDIENCE_ALL && filteredGroups.length > 0) {
          // Fetch trends for each filtered group and merge (average by label; only count groups that have that label)
          const allResponses = await Promise.all(
            filteredGroups.map((g) =>
              getGroupTrends(TOKEN, g.id, 'all', false, startDateStr, endDateStr)
            )
          );
          const allTrends = allResponses.map((r) => r.data.trends);
          const labelMap = new Map<string, { current: number; previous: number; count: number; n: number }>();
          for (const trendList of allTrends) {
            for (const point of trendList) {
              const existing = labelMap.get(point.label);
              if (existing) {
                existing.current += point.current;
                existing.previous = (existing.previous ?? 0) + (point.previous ?? 0);
                existing.count += point.count;
                existing.n += 1;
              } else {
                labelMap.set(point.label, {
                  current: point.current,
                  previous: point.previous ?? 0,
                  count: point.count,
                  n: 1
                });
              }
            }
          }
          const merged: TrendsDataPoint[] = [];
          labelMap.forEach((v, label) => {
            const n = v.n;
            merged.push({
              label,
              current: n > 0 ? Math.round((v.current / n) * 10) / 10 : 0,
              previous: n > 0 && v.previous ? Math.round((v.previous / n) * 10) / 10 : null,
              count: v.count
            });
          });
          merged.sort((a, b) => a.label.localeCompare(b.label));
          setTrends(merged);
          return;
        }

        if (!selectedGroupId || selectedGroupId === AUDIENCE_ALL) {
          setTrends([]);
          return;
        }

        const response = await getGroupTrends(
          TOKEN,
          selectedGroupId,
          'all',
          false,
          startDateStr,
          endDateStr
        );
        setTrends(response.data.trends);
      } catch (e: any) {

        setTrends([]);
        setTrendsError(e?.message || 'Failed to load group trends.');
      } finally {
        setTrendsLoading(false);
      }
    };

    fetchTrends();
  }, [navigate, selectedGroupId, dateRangeStart, dateRangeEnd, groups, filteredGroups, token]);



  // From calendar: single-date selection sets start; constrain end if needed
  const handleFromDateSelect = (date: Date) => {
    setDateRangeStart(date);
    if (dateRangeEnd && date > dateRangeEnd) {
      setDateRangeEnd(date);
    }
  };

  // To calendar: single-date selection sets end; ensure To >= From
  const handleToDateSelect = (date: Date) => {
    if (dateRangeStart && date < dateRangeStart) {
      setDateRangeEnd(dateRangeStart);
      setDateRangeStart(date);
    } else {
      setDateRangeEnd(date);
    }
  };

  // Check if a date is within the selected range
  const isDateInRange = (date: Date): boolean => {
    if (!dateRangeStart || !dateRangeEnd) return false;
    return date >= dateRangeStart && date <= dateRangeEnd;
  };

  // Clear date range
  const handleClearDateRange = () => {
    const defaultEnd = new Date();
    const defaultStart = new Date();
    defaultStart.setDate(defaultStart.getDate() - 30);
    setDateRangeStart(defaultStart);
    setDateRangeEnd(defaultEnd);
  };

  const handleCalendarNavigationFrom = (direction: 'prev' | 'next') => {
    const newDate = addMonths(calendarCursorFrom, direction === 'prev' ? -1 : 1);
    if (newDate) setCalendarCursorFrom(newDate);
  };

  const handleCalendarNavigationTo = (direction: 'prev' | 'next') => {
    const newDate = addMonths(calendarCursorTo, direction === 'prev' ? -1 : 1);
    if (newDate) setCalendarCursorTo(newDate);
  };

  const handleExportCSV = () => {
    if (trends.length === 0) {
      alert('No data to export');
      return;
    }

    const csvContent = [
      'Date,Current Period,Previous Period',
      ...trends.map((point: TrendsDataPoint) => `${point.label},${point.current},${point.previous || ''}`)
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'wellness-trends.csv';
    link.click();
  };

  const handleExportPDF = () => {
    // Simple PDF export - in real implementation, use jsPDF
    window.print();
  };

  // Derive metrics from trends (for chosen group and time frame) and overview (for group-level counts)
  const metrics = useMemo(() => {
    const hasTrends = trends.length > 0;
    const hasOverview = overview != null;
    const approvedMembers = hasOverview ? overview.counts.approvedMembers : 0;

    // Range-based from trends when available
    const trendsWellbeingAvg = hasTrends
      ? trends.reduce((sum, p) => sum + p.current, 0) / trends.length
      : 0;
    const trendsAssessmentsTotal = hasTrends
      ? trends.reduce((sum, p) => sum + p.count, 0)
      : 0;
    const participationFromRange =
      hasTrends && hasOverview && approvedMembers > 0
        ? Math.round((trendsAssessmentsTotal / approvedMembers) * 100)
        : null;

    // Fallbacks from overview when no trends
    const overviewParticipation = hasOverview && approvedMembers > 0
      ? Math.round((overview.counts.withAssessments / approvedMembers) * 100)
      : 0;
    const overviewWellbeing = hasOverview ? overview.averages.overall0to4 * 25 : 0;
    const overviewAssessments = hasOverview ? overview.counts.withAssessments : 0;

    const participationRate = participationFromRange ?? overviewParticipation;
    const participationValue =
      selectedGroupId === AUDIENCE_ALL || (!hasOverview && !hasTrends)
        ? '—'
        : `${Math.min(100, participationRate)}%`;
    const participationChange =
      selectedGroupId === AUDIENCE_ALL ? 'N/A' : participationRate > 0 ? 'Active' : 'No data';

    const wellbeingScore = hasTrends ? trendsWellbeingAvg : overviewWellbeing;
    const wellbeingValue = hasTrends || hasOverview
      ? `${wellbeingScore.toFixed(1)}/100`
      : 'No data';
    const wellbeingChange = wellbeingScore > 0 ? 'Active' : 'No data';

    const assessmentsCount = hasTrends ? trendsAssessmentsTotal : overviewAssessments;
    const assessmentsValue = hasTrends || hasOverview ? assessmentsCount.toString() : '0';
    const assessmentsChange = assessmentsCount > 0 ? 'Active' : 'No data';

    const membersValue =
      selectedGroupId === AUDIENCE_ALL
        ? 'All groups'
        : hasOverview
          ? approvedMembers.toString()
          : '—';
    const membersChange =
      selectedGroupId === AUDIENCE_ALL ? 'N/A' : approvedMembers > 0 ? 'Active' : 'No data';

    return [
      {
        id: 'participation',
        title: 'Participation Rate',
        value: participationValue,
        change: participationChange,
        changeType: participationRate > 0 ? 'positive' : 'neutral' as const,
        icon: 'group'
      },
      {
        id: 'wellbeing',
        title: 'Overall Well-being',
        value: wellbeingValue,
        change: wellbeingChange,
        changeType: wellbeingScore > 0 ? 'positive' : 'neutral' as const,
        icon: 'analytics'
      },
      {
        id: 'assessments',
        title: 'Assessments Completed',
        value: assessmentsValue,
        change: assessmentsChange,
        changeType: assessmentsCount > 0 ? 'positive' : 'neutral' as const,
        icon: 'vital_signs'
      },
      {
        id: 'members',
        title: 'Group Members',
        value: membersValue,
        change: membersChange,
        changeType: approvedMembers > 0 ? 'positive' : 'neutral' as const,
        icon: 'group'
      }
    ];
  }, [overview, trends, selectedGroupId]);

  const handleGroupChange = (groupId: string) => {
    setSelectedGroupId(groupId || null);
    localStorage.setItem(GROUP_STORAGE_KEY, groupId || '');
  };

  // Derive department breakdown from overview data
  const departmentData = useMemo(() => {
    if (!overview) {
      return [
        { name: 'No Data Available', percentage: 0 }
      ];
    }

    const { averages } = overview;
    const attributeLabels: Record<string, string> = {
      spiritual: 'Spiritual',
      nutritional: 'Nutritional',
      physical: 'Physical',
      emotioPsychoRational: 'Emotio-Psycho-Rational',
      legendarial: 'Legendarial',
      religioTraditioCultural: 'Religio-Traditio-Cultural',
      financial: 'Financial',
      environmental: 'Environmental',
      socioRelational: 'Socio-Relational',
      occupational: 'Occupational'
    };

    return Object.entries(averages.attributes0to4)
      .filter(([_, score]) => score > 0)
      .map(([key, score]) => ({
        name: attributeLabels[key] || key,
        percentage: Math.round((score / 4) * 100) // Convert 0-4 scale to 0-100 percentage
      }))
      .sort((a, b) => b.percentage - a.percentage); // Sort by percentage descending
  }, [overview]);


  const handleRefresh = async () => {
    if (!dateRangeStart || !dateRangeEnd) return;

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const startDateStr = dateRangeStart.toISOString().split('T')[0];
    const endDateStr = dateRangeEnd.toISOString().split('T')[0];

    try {
      setOverviewLoading(true);
      setTrendsLoading(true);
      setOverviewError(null);
      setTrendsError(null);

      if (selectedGroupId === AUDIENCE_ALL && groups.length > 0) {
        const allResponses = await Promise.all(
          groups.map((g) =>
            getGroupTrends(token, g.id, 'all', false, startDateStr, endDateStr)
          )
        );
        const allTrends = allResponses.map((r) => r.data.trends);
        const labelMap = new Map<string, { current: number; previous: number; count: number; n: number }>();
        for (const trendList of allTrends) {
          for (const point of trendList) {
            const existing = labelMap.get(point.label);
            if (existing) {
              existing.current += point.current;
              existing.previous = (existing.previous ?? 0) + (point.previous ?? 0);
              existing.count += point.count;
              existing.n += 1;
            } else {
              labelMap.set(point.label, {
                current: point.current,
                previous: point.previous ?? 0,
                count: point.count,
                n: 1
              });
            }
          }
        }
        const merged: TrendsDataPoint[] = [];
        labelMap.forEach((v, label) => {
          const n = v.n;
          merged.push({
            label,
            current: n > 0 ? Math.round((v.current / n) * 10) / 10 : 0,
            previous: n > 0 && v.previous ? Math.round((v.previous / n) * 10) / 10 : null,
            count: v.count
          });
        });
        merged.sort((a, b) => a.label.localeCompare(b.label));
        setTrends(merged);
      } else if (selectedGroupId && selectedGroupId !== AUDIENCE_ALL) {
        const [overviewResponse, trendsResponse] = await Promise.all([
          getGroupOverview(token, selectedGroupId),
          getGroupTrends(token, selectedGroupId, 'all', false, startDateStr, endDateStr)
        ]);
        setOverview(overviewResponse.data);
        setTrends(trendsResponse.data.trends);
      }
    } catch (e: any) {

      setOverviewError(e?.message || 'Failed to refresh data.');
      setTrendsError(e?.message || 'Failed to refresh trends.');
    } finally {
      setOverviewLoading(false);
      setTrendsLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="consultant-reports__chart-tooltip">
          <p className="consultant-reports__chart-tooltip-date">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className={`consultant-reports__chart-tooltip-value consultant-reports__chart-tooltip-value--${entry.dataKey}`}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`consultant-reports ${isDarkMode ? 'consultant-reports--dark' : ''}`}>
      <div className="consultant-reports__layout">
        {/* Header */}
        <header className="consultant-reports__header">
          <div className="consultant-reports__header-content">
            <div className="consultant-reports__title-section">
              <h1 className="consultant-reports__title">Reports &amp; Analytics</h1>
              <p className="consultant-reports__subtitle">Consultant Dashboard - Well-being Score Analysis</p>
            </div>
            <div className="consultant-reports__header-actions">
              {(reportOwnership !== 'enterprise_owned' || scope === 'enterprise') && (
                groupsLoading ? (
                  <div className="consultant-reports__loading">Loading groups...</div>
                ) : groups.length > 0 ? (
                  <SelectDropdown
                    value={selectedGroupId || ''}
                    onChange={(value) => handleGroupChange(value)}
                    options={[
                      { value: AUDIENCE_ALL, label: scope === 'enterprise' ? 'All Groups' : 'All' },
                      ...(scope === 'enterprise' ? filteredGroups : groups).map((group) => ({
                        value: group.id,
                        label: group.groupName
                      }))
                    ]}
                    placeholder="Select a group"
                    ariaLabel="Select group"
                  />
                ) : null
              )}
            </div>
          </div>
        </header>

        {/* Metrics Cards */}
        <section className="consultant-reports__metrics">
          {metrics.map((metric) => (
            <div key={metric.id} className="consultant-reports__metric-card">
              <div className="consultant-reports__metric-header">
                <p className="consultant-reports__metric-title">{metric.title}</p>
                <span className="material-symbols-outlined consultant-reports__metric-icon">{metric.icon}</span>
              </div>
              <div className="consultant-reports__metric-value">
                <p className="consultant-reports__metric-number">{metric.value}</p>
                <div className="consultant-reports__metric-change">
                  <span className={`material-symbols-outlined consultant-reports__metric-change-icon ${metric.changeType === 'positive' ? 'consultant-reports__metric-change-icon--positive' :
                    metric.changeType === 'negative' ? 'consultant-reports__metric-change-icon--negative' :
                      'consultant-reports__metric-change-icon--neutral'
                    }`}>
                    {metric.changeType === 'positive' ? 'trending_up' :
                      metric.changeType === 'negative' ? 'trending_down' : 'trending_flat'}
                  </span>
                  <p className={`consultant-reports__metric-change-text consultant-reports__metric-change-text--${metric.changeType}`}>
                    {metric.change}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </section>

        <div className="consultant-reports__main-content">
          {/* Report Builder Sidebar */}
          <div className="consultant-reports__sidebar">
            <div className="consultant-reports__builder">
              <div className="consultant-reports__builder-header">
                <h2 className="consultant-reports__builder-title">
                  <span className="material-symbols-outlined consultant-reports__builder-icon">tune</span>
                  Custom Report Builder
                </h2>
              </div>
              <div className="consultant-reports__builder-content">
                {/* Report ownership: Locally owned / Enterprise owned - only for consultant scope */}
                {scope !== 'enterprise' && (
                  <div className="consultant-reports__ownership">
                    <span className="consultant-reports__ownership-label">Report type</span>
                    <div className="consultant-reports__ownership-options">
                      <button
                        type="button"
                        className={`consultant-reports__ownership-option consultant-reports__ownership-option--${reportOwnership === 'locally_owned' ? 'active' : 'inactive'}`}
                        onClick={() => setReportOwnership('locally_owned')}
                      >
                        Locally owned
                      </button>
                      <button
                        type="button"
                        className={`consultant-reports__ownership-option consultant-reports__ownership-option--${reportOwnership === 'enterprise_owned' ? 'active' : 'inactive'}`}
                        onClick={() => setReportOwnership('enterprise_owned')}
                      >
                        Enterprise owned
                      </button>
                    </div>
                  </div>
                )}

                {/* Course / Year filters when Enterprise owned — for enterprise scope only shown for school/university groups */}
                {reportOwnership === 'enterprise_owned' && (scope !== 'enterprise' || selectedGroup?.groupCategory === 'student') && (
                  <>
                    <div className="consultant-reports__group-selector">
                      <span className="consultant-reports__group-selector-label">Course</span>
                      <SelectDropdown
                        value={reportCourseFilter}
                        onChange={(next) => {
                          setReportCourseFilter(next);
                          const years = next === 'all' ? yearOptionsWhenAllCourses : (studentOptions.yearByCourse[next] || []);
                          if (reportYearFilter !== 'all' && years.length > 0 && !years.includes(reportYearFilter)) {
                            setReportYearFilter('all');
                          }
                        }}
                        options={[
                          { value: 'all', label: 'All course' },
                          ...studentOptions.courses.map((c) => ({
                            value: c,
                            label: c
                          }))
                        ]}
                        placeholder="Select a course"
                        ariaLabel="Filter by course"
                      />
                    </div>
                    <div className="consultant-reports__group-selector">
                      <span className="consultant-reports__group-selector-label">Year</span>
                      <SelectDropdown
                        value={reportYearFilter}
                        onChange={(value) => setReportYearFilter(value)}
                        options={[
                          { value: 'all', label: 'All year' },
                          ...(reportCourseFilter === 'all' ? yearOptionsWhenAllCourses : (studentOptions.yearByCourse[reportCourseFilter] || [])).map((y) => ({
                            value: y,
                            label: y
                          }))
                        ]}
                        placeholder="Select a year"
                        ariaLabel="Filter by year"
                      />
                    </div>
                  </>
                )}

                {/* Audience Segment: shown in sidebar only for locally owned consultant scope */}
                {reportOwnership !== 'enterprise_owned' && scope !== 'enterprise' && (
                  <div className="consultant-reports__group-selector">
                    <span className="consultant-reports__group-selector-label">Audience Segment</span>
                    {groupsLoading ? (
                      <div className="consultant-reports__loading">Loading groups...</div>
                    ) : groupsError ? (
                      <div className="consultant-reports__error">{groupsError}</div>
                    ) : groups.length === 0 ? (
                      <div className="consultant-reports__empty">No groups available</div>
                    ) : (
                      <SelectDropdown
                        value={selectedGroupId || ''}
                        onChange={(value) => handleGroupChange(value)}
                        options={[
                          { value: AUDIENCE_ALL, label: 'All' },
                          ...groups.map((group) => ({
                            value: group.id,
                            label: group.groupName
                          }))
                        ]}
                        placeholder="Select a group"
                        ariaLabel="Select audience segment"
                      />
                    )}
                  </div>
                )}

                {/* Advanced Filters: only for Locally owned */}
                {reportOwnership === 'locally_owned' && (
                  <div className="consultant-reports__filters">
                    <div className="consultant-reports__filters-header">
                      <span className="consultant-reports__filters-title">Advanced Filters</span>
                      {selectedFilterIds.length > 0 && (
                        <button
                          className="consultant-reports__filters-clear"
                          onClick={handleClearAllFilters}
                        >
                          Clear All
                        </button>
                      )}
                    </div>

                    {/* Selected Filter Chips */}
                    <div className="consultant-reports__filters-list">
                      {selectedFilterIds.map((id) => {
                        const name = findNodeName(id, folderTree) || 'Unknown';
                        return (
                          <button
                            key={id}
                            className="consultant-reports__filter-chip consultant-reports__filter-chip--active"
                            onClick={() => handleToggleFilter(id)}
                          >
                            {name}
                            <span className="material-symbols-outlined consultant-reports__filter-chip-icon">close</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Show All Filters Button */}
                    <button
                      className="consultant-reports__show-all-filters-btn"
                      onClick={() => setFilterModalOpen(true)}
                    >
                      <span className="material-symbols-outlined">filter_list</span>
                      Show All Filters
                    </button>

                    {/* Filter Modal */}
                    <Modal
                      isOpen={filterModalOpen}
                      onClose={() => setFilterModalOpen(false)}
                      title="Select Filters"
                      className="consultant-reports__filter-modal-container"
                    >
                      <div className="consultant-reports__filter-modal">
                        {/* Search Bar */}
                        <div className="consultant-reports__filter-search">
                          <span className="material-symbols-outlined">search</span>
                          <input
                            type="text"
                            placeholder="Search folders and groups..."
                            value={filterSearchQuery}
                            onChange={(e) => setFilterSearchQuery(e.target.value)}
                          />
                        </div>

                        {/* Tree View */}
                        <div className="consultant-reports__filter-tree">
                          {folderTreeLoading ? (
                            <div className="consultant-reports__loading">Loading structure...</div>
                          ) : displayedTree.length === 0 ? (
                            <div className="consultant-reports__empty">No folders or groups found</div>
                          ) : (
                            renderTreeNodes(displayedTree)
                          )}
                        </div>
                      </div>
                    </Modal>
                  </div>
                )}

                {/* Period Selection: From and To clickable inputs */}
                <div className="consultant-reports__period">
                  <div className="consultant-reports__period-header">
                    <span className="consultant-reports__period-title">Time frame</span>
                    {dateRangeStart && dateRangeEnd && (
                      <button
                        className="consultant-reports__period-clear"
                        onClick={handleClearDateRange}
                      >
                        Clear Range
                      </button>
                    )}
                  </div>
                  {dateRangeStart && dateRangeEnd && (
                    <div className="consultant-reports__period-info">
                      <p className="consultant-reports__period-info-text">
                        From {dateRangeStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} to {dateRangeEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                  )}
                  <div className="consultant-reports__date-inputs">
                    <div className="consultant-reports__date-input-row">
                      <span className="consultant-reports__period-label">From</span>
                      <button
                        type="button"
                        className="consultant-reports__date-input consultant-reports__date-input--from"
                        onClick={() => setModalFromOpen(true)}
                        aria-label="Select From date"
                      >
                        {dateRangeStart
                          ? dateRangeStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : 'Select date'}
                      </button>
                    </div>
                    <div className="consultant-reports__date-input-row">
                      <span className="consultant-reports__period-label">To</span>
                      <button
                        type="button"
                        className="consultant-reports__date-input consultant-reports__date-input--to"
                        onClick={() => setModalToOpen(true)}
                        aria-label="Select To date"
                      >
                        {dateRangeEnd
                          ? dateRangeEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : 'Select date'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* From date modal */}
                <Modal
                  isOpen={modalFromOpen}
                  onClose={() => setModalFromOpen(false)}
                  title="From"
                  className="consultant-reports__modal"
                >
                  <div className="consultant-reports__modal-calendar">
                    <div className="consultant-reports__calendar">
                      <div className="consultant-reports__calendar-header">
                        <button
                          className={`consultant-reports__calendar-nav ${!canNavigate('prev', calendarCursorFrom) ? 'consultant-reports__calendar-nav--disabled' : ''
                            }`}
                          onClick={() => handleCalendarNavigationFrom('prev')}
                          disabled={!canNavigate('prev', calendarCursorFrom)}
                        >
                          <span className="material-symbols-outlined">chevron_left</span>
                        </button>
                        <span className="consultant-reports__calendar-month">
                          {getMonthYearDisplay(calendarCursorFrom)}
                        </span>
                        <button
                          className={`consultant-reports__calendar-nav ${!canNavigate('next', calendarCursorFrom) ? 'consultant-reports__calendar-nav--disabled' : ''
                            }`}
                          onClick={() => handleCalendarNavigationFrom('next')}
                          disabled={!canNavigate('next', calendarCursorFrom)}
                        >
                          <span className="material-symbols-outlined">chevron_right</span>
                        </button>
                      </div>
                      <div className="consultant-reports__calendar-days">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                          <span key={day} className="consultant-reports__calendar-day-label">{day}</span>
                        ))}
                      </div>
                      <div className="consultant-reports__calendar-grid">
                        {generateCalendarGrid(calendarCursorFrom).map((calendarDay, index) => {
                          const dayDate = new Date(calendarCursorFrom.getFullYear(), calendarCursorFrom.getMonth(), calendarDay.day);
                          const isStart = dateRangeStart && calendarDay.isCurrentMonth && dayDate.toDateString() === dateRangeStart.toDateString();
                          return (
                            <button
                              key={index}
                              type="button"
                              className={`consultant-reports__calendar-day ${calendarDay.isCurrentMonth ? '' : 'consultant-reports__calendar-day--empty'
                                } ${isStart ? 'consultant-reports__calendar-day--range-endpoint' : ''}`}
                              onClick={() => calendarDay.isCurrentMonth && handleFromDateSelect(dayDate)}
                              disabled={!calendarDay.isCurrentMonth}
                              title={calendarDay.isCurrentMonth ? dayDate.toDateString() : ''}
                            >
                              {calendarDay.isCurrentMonth ? calendarDay.day : ''}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <p className="consultant-reports__modal-date-summary">
                      From: {dateRangeStart
                        ? dateRangeStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : '—'}
                    </p>
                  </div>
                </Modal>

                {/* To date modal */}
                <Modal
                  isOpen={modalToOpen}
                  onClose={() => setModalToOpen(false)}
                  title="To"
                  className="consultant-reports__modal"
                >
                  <div className="consultant-reports__modal-calendar">
                    <div className="consultant-reports__calendar">
                      <div className="consultant-reports__calendar-header">
                        <button
                          className={`consultant-reports__calendar-nav ${!canNavigate('prev', calendarCursorTo) ? 'consultant-reports__calendar-nav--disabled' : ''
                            }`}
                          onClick={() => handleCalendarNavigationTo('prev')}
                          disabled={!canNavigate('prev', calendarCursorTo)}
                        >
                          <span className="material-symbols-outlined">chevron_left</span>
                        </button>
                        <span className="consultant-reports__calendar-month">
                          {getMonthYearDisplay(calendarCursorTo)}
                        </span>
                        <button
                          className={`consultant-reports__calendar-nav ${!canNavigate('next', calendarCursorTo) ? 'consultant-reports__calendar-nav--disabled' : ''
                            }`}
                          onClick={() => handleCalendarNavigationTo('next')}
                          disabled={!canNavigate('next', calendarCursorTo)}
                        >
                          <span className="material-symbols-outlined">chevron_right</span>
                        </button>
                      </div>
                      <div className="consultant-reports__calendar-days">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                          <span key={day} className="consultant-reports__calendar-day-label">{day}</span>
                        ))}
                      </div>
                      <div className="consultant-reports__calendar-grid">
                        {generateCalendarGrid(calendarCursorTo).map((calendarDay, index) => {
                          const dayDate = new Date(calendarCursorTo.getFullYear(), calendarCursorTo.getMonth(), calendarDay.day);
                          const isEnd = dateRangeEnd && calendarDay.isCurrentMonth && dayDate.toDateString() === dateRangeEnd.toDateString();
                          return (
                            <button
                              key={index}
                              type="button"
                              className={`consultant-reports__calendar-day ${calendarDay.isCurrentMonth ? '' : 'consultant-reports__calendar-day--empty'
                                } ${isEnd ? 'consultant-reports__calendar-day--range-endpoint' : ''}`}
                              onClick={() => calendarDay.isCurrentMonth && handleToDateSelect(dayDate)}
                              disabled={!calendarDay.isCurrentMonth}
                              title={calendarDay.isCurrentMonth ? dayDate.toDateString() : ''}
                            >
                              {calendarDay.isCurrentMonth ? calendarDay.day : ''}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <p className="consultant-reports__modal-date-summary">
                      To: {dateRangeEnd
                        ? dateRangeEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : '—'}
                    </p>
                  </div>
                </Modal>

                {/* Update Button */}
                <button className="consultant-reports__update-btn">
                  Update Report
                </button>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="consultant-reports__content-area">
            {/* Trend Analysis Chart */}
            <div className="consultant-reports__chart-section">
              <div className="consultant-reports__chart-header">
                <div className="consultant-reports__chart-info">
                  <h2 className="consultant-reports__chart-title">Trend Analysis</h2>
                  <p className="consultant-reports__chart-subtitle">
                    {selectedGroupId === AUDIENCE_ALL ? 'All groups' : selectedGroup ? selectedGroup.groupName : ''}
                    {selectedGroupId === AUDIENCE_ALL || selectedGroup ? ' - ' : ''}
                    {dateRangeStart && dateRangeEnd
                      ? `From ${dateRangeStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} to ${dateRangeEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                      : 'Select From and To dates'}
                  </p>
                </div>
                <div className="consultant-reports__chart-actions">
                  <button
                    className="consultant-reports__chart-action-btn"
                    onClick={handleExportCSV}
                    title="Download CSV"
                  >
                    <span className="material-symbols-outlined">csv</span>
                  </button>
                  <button
                    className="consultant-reports__chart-action-btn"
                    onClick={() => window.print()}
                    title="Print Report"
                  >
                    <span className="material-symbols-outlined">print</span>
                  </button>
                  <button
                    className="consultant-reports__chart-action-btn consultant-reports__chart-action-btn--primary"
                    onClick={handleExportPDF}
                  >
                    <span className="material-symbols-outlined">download</span>
                    <span className="consultant-reports__chart-action-text">Export PDF</span>
                  </button>
                </div>
              </div>
              <div className="consultant-reports__chart-container">
                {trendsLoading ? (
                  <div className="consultant-reports__loading">Loading trends data...</div>
                ) : trendsError ? (
                  <div className="consultant-reports__error">{trendsError}</div>
                ) : trends.length === 0 ? (
                  <div className="consultant-reports__empty">No trends data available</div>
                ) : (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart
                      data={trends}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#d1d5db" />
                      <XAxis
                        dataKey="label"
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        fontSize={12}
                        stroke="#374151"
                      />
                      <YAxis
                        domain={[0, 100]}
                        fontSize={12}
                        stroke="#374151"
                        label={{ value: 'Well-being Score (%)', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="current"
                        stroke="#2bd4bd"
                        strokeWidth={3}
                        name="Current Period"
                        dot={{ fill: '#2bd4bd', strokeWidth: 2, r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div className="consultant-reports__chart-legend">
                <div className="consultant-reports__legend-item">
                  <div className="consultant-reports__legend-color consultant-reports__legend-color--current"></div>
                  <span className="consultant-reports__legend-text">Current Period</span>
                </div>
              </div>
            </div>

            {/* Well-being Attributes Breakdown */}
            <div className="consultant-reports__breakdown">
              <div className="consultant-reports__breakdown-header">
                <h3 className="consultant-reports__breakdown-title">Well-being Attributes Breakdown</h3>
              </div>
              <div className="consultant-reports__breakdown-list">
                {departmentData.map((attr) => (
                  <div key={attr.name} className="consultant-reports__breakdown-item">
                    <div className="consultant-reports__breakdown-name">{attr.name}</div>
                    <div className="consultant-reports__breakdown-bar">
                      <div
                        className={`consultant-reports__breakdown-progress consultant-reports__breakdown-progress--p${Math.round(attr.percentage / 10) * 10}`}
                      ></div>
                    </div>
                    <div className="consultant-reports__breakdown-percentage">{attr.percentage}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultantReportsPage;

